/**
 * Graph processing utilities for word expansion results.
 */

/** Max new nodes on first expansion of a word */
export const MAX_NEW_NODES_FIRST = 5;

/** Max new nodes when re-expanding an already-expanded word */
export const MAX_NEW_NODES_REEXPAND = 2;

/**
 * Convert API expansion results into graph-ready nodes and links.
 *
 * Only creates links to nodes that already exist or will be added,
 * preventing the force graph from receiving dangling references.
 */
export function processExpansionResults(
  clickedWord,
  similarWords,
  crossLinks,
  existingIds,
  targetWord,
  isTargetReached,
  isReexpansion = false,
) {
  const newNodes = [];
  const newLinks = [];
  const allNodeIds = new Set(existingIds);
  const maxNew = isReexpansion ? MAX_NEW_NODES_REEXPAND : MAX_NEW_NODES_FIRST;

  // Put target first so it's guaranteed a slot when reached
  const ordered = [...similarWords];
  if (isTargetReached && targetWord) {
    const idx = ordered.findIndex(sw => sw.word === targetWord);
    if (idx > 0) ordered.unshift(ordered.splice(idx, 1)[0]);
  }

  // Decide which words to keep (existing nodes + up to maxNew new ones)
  let added = 0;
  const kept = [];

  for (const sw of ordered) {
    if (!sw?.word) continue;
    if (existingIds.has(sw.word)) {
      kept.push({ ...sw, isNew: false });
    } else if (added < maxNew) {
      kept.push({ ...sw, isNew: true });
      allNodeIds.add(sw.word);
      added++;
    }
  }

  // Build nodes and links
  for (const item of kept) {
    newLinks.push({
      source: clickedWord,
      target: item.word,
      similarity: item.similarity,
    });
    if (item.isNew) {
      newNodes.push({
        id: item.word,
        word: item.word,
        emoji: item.emoji || '',
        similarity: item.similarity,
        isExpanded: false,
      });
    }
  }

  // Cross-links only between nodes that will exist in the graph
  for (const link of crossLinks || []) {
    if (allNodeIds.has(link.word1) && allNodeIds.has(link.word2)) {
      newLinks.push({
        source: link.word1,
        target: link.word2,
        similarity: link.similarity,
      });
    }
  }

  return { newNodes, newLinks };
}

/**
 * Find the shortest path between two nodes using BFS.
 * Returns an array of node IDs forming the path, or null if no path exists.
 */
export function findShortestPath(startId, targetId, links) {
  if (startId === targetId) return [startId];

  // Build adjacency list
  const adj = new Map();
  for (const link of links) {
    const s = typeof link.source === 'object' ? link.source?.id : link.source;
    const t = typeof link.target === 'object' ? link.target?.id : link.target;
    if (!s || !t) continue;
    if (!adj.has(s)) adj.set(s, []);
    if (!adj.has(t)) adj.set(t, []);
    adj.get(s).push(t);
    adj.get(t).push(s);
  }

  // BFS with parent tracking
  const parent = new Map();
  const visited = new Set([startId]);
  const queue = [startId];

  while (queue.length) {
    const id = queue.shift();
    for (const nb of adj.get(id) || []) {
      if (visited.has(nb)) continue;
      visited.add(nb);
      parent.set(nb, id);
      if (nb === targetId) {
        // Reconstruct path
        const path = [targetId];
        let cur = targetId;
        while (cur !== startId) {
          cur = parent.get(cur);
          path.unshift(cur);
        }
        return path;
      }
      queue.push(nb);
    }
  }

  return null; // No path found
}

/**
 * Convert a path (array of node IDs) into a Set of edge keys for quick lookup.
 * Edge key format: "nodeA||nodeB" where nodeA < nodeB (lexicographic order).
 */
export function pathToEdgeSet(path) {
  if (!path || path.length < 2) return new Set();
  const edges = new Set();
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    edges.add(a < b ? `${a}||${b}` : `${b}||${a}`);
  }
  return edges;
}
